import {
  type PendingApprovalResolutionType,
  parseMessageArtifacts,
} from "#src/view-models/investigations";
import {
  Button,
  RichTextMarkdownDataDisplay,
} from "@validereinc/common-components";
import {
  ApprovalDecision,
  InvestigationMessageRole,
  InvestigationMessageKind,
  InvestigationStatus,
  type InvestigationMessageType,
  type InvestigationStatusType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import { MessageArtifactList } from "./MessageArtifactList";
import {
  PendingApprovalCard,
  type PendingApprovalCardDecisionType,
} from "./PendingApprovalCard";
import { buildApprovalFields } from "./approvalFields";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

export type ConversationFeedProps = {
  messages: InvestigationMessageType[];
  status: InvestigationStatusType;
  report: string | null;
  pendingApprovalMessageId?: string | null;
  resolutionsByPendingApprovalId?: Map<string, PendingApprovalResolutionType>;
  isApprovalLoading?: boolean;
  hasOlderMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  onApprovalDecision?: (decision: PendingApprovalCardDecisionType) => void;
  /** When set, the matching message bubble briefly flashes to draw the eye. */
  highlightedMessageId?: string | null;
  onLoadOlder?: () => void;
};

const NO_REPORT_PLACEHOLDER = "No report produced.";

export const ConversationFeed = ({
  messages,
  status,
  report,
  pendingApprovalMessageId = null,
  resolutionsByPendingApprovalId = new Map(),
  isApprovalLoading = false,
  hasOlderMessages = false,
  isLoadingOlderMessages = false,
  onApprovalDecision,
  highlightedMessageId = null,
  onLoadOlder,
}: ConversationFeedProps) => {
  const hasAnyAgentMessage = messages.some(
    (message) => message.role === InvestigationMessageRole.AGENT
  );
  const showNoReportPlaceholder =
    status === InvestigationStatus.DONE && !report && !hasAnyAgentMessage;

  return (
    <div
      className={cx("conversation-feed")}
      data-testid="conversation-feed"
    >
      {hasOlderMessages ? (
        <div className={cx("conversation-feed__load-older")}>
          <Button
            variant="outline"
            size="small"
            isLoading={isLoadingOlderMessages}
            onClick={onLoadOlder}
          >
            Load older messages
          </Button>
        </div>
      ) : null}
      {messages.map((message) => {
        if (
          message.kind === InvestigationMessageKind.APPROVAL_DECISION &&
          !message.content?.trim()
        ) {
          return null;
        }

        const isHighlighted = message.id === highlightedMessageId;
        const resolution = resolutionsByPendingApprovalId.get(message.id);
        const artifacts = parseMessageArtifacts(message.metadata);
        return (
          <div
            key={message.id}
            className={cx("conversation-feed__entry", {
              "conversation-feed__entry--user":
                message.role === InvestigationMessageRole.USER,
              "conversation-feed__entry--agent":
                message.role === InvestigationMessageRole.AGENT,
            })}
            data-testid={`conversation-entry-${message.id}`}
          >
            <div
              className={cx("conversation-feed__bubble", {
                "conversation-feed__bubble--user":
                  message.role === InvestigationMessageRole.USER,
                "conversation-feed__bubble--agent":
                  message.role === InvestigationMessageRole.AGENT,
                "conversation-feed__bubble--pending-approval":
                  message.kind === InvestigationMessageKind.PENDING_APPROVAL,
                "conversation-feed__bubble--flash": isHighlighted,
              })}
              data-testid={`conversation-message-${message.role}`}
              data-highlighted={isHighlighted ? "true" : undefined}
            >
              <div className={cx("conversation-feed__role-label")}>
                {message.role === InvestigationMessageRole.USER
                  ? "You"
                  : "Agent"}
              </div>
              {message.role === InvestigationMessageRole.AGENT &&
              message.kind === InvestigationMessageKind.PENDING_APPROVAL ? (
                <PendingApprovalCard
                  fields={buildApprovalFields(message.metadata)}
                  messageContent={message.content}
                  decision={resolution?.decision ?? undefined}
                  isResolved={Boolean(resolution)}
                  isReadOnly={message.id !== pendingApprovalMessageId}
                  isLoading={isApprovalLoading}
                  onDecide={(decision) => {
                    if (!onApprovalDecision) return;
                    if (
                      decision !== ApprovalDecision.APPROVE &&
                      decision !== ApprovalDecision.REJECT
                    ) {
                      return;
                    }
                    onApprovalDecision(decision);
                  }}
                />
              ) : message.role === InvestigationMessageRole.AGENT ? (
                <RichTextMarkdownDataDisplay
                  value={message.content}
                  isInline={false}
                  isContainerSizing
                />
              ) : (
                message.content
              )}
            </div>
            <MessageArtifactList artifacts={artifacts} />
          </div>
        );
      })}
      {showNoReportPlaceholder ? (
        <div
          className={cx(
            "conversation-feed__bubble",
            "conversation-feed__bubble--agent",
            "conversation-feed__placeholder"
          )}
        >
          {NO_REPORT_PLACEHOLDER}
        </div>
      ) : null}
    </div>
  );
};
